<div class="page">
  <div class="page-head">
    <div>
      <h2>{{ pageTitle() || 'Communication Provider Config' }}</h2>
      <p>Provider credentials per channel — the HOW of sending. A partner row overrides the Global default.</p>
    </div>
    <p-button label="New Config" icon="pi pi-plus" (onClick)="openAdd()" />
  </div>

  <div class="card">
    @if (dupActiveGlobalChannels().length) {
      <!-- Only ONE active global row per channel is read at send time, and which of two wins
           is undefined — sends would behave randomly depending on row order. -->
      <div class="dup-banner">
        <i class="pi pi-exclamation-triangle"></i>
        More than one <b>active global</b> config for:
        {{ dupActiveGlobalChannels().join(', ') }} — only one is used per channel, and which
        one is undefined. Deactivate the extras.
      </div>
    }
    <div class="toolbar">
      <p-iconfield iconPosition="left" class="search-field">
        <p-inputicon styleClass="pi pi-search" />
        <input type="text" pInputText placeholder="Search provider, channel, sender, scope..."
          [ngModel]="search()" (ngModelChange)="search.set($event)">
      </p-iconfield>
      <div class="filter">
        <label>Channel</label>
        <p-select [ngModel]="channelFilter()" (ngModelChange)="onChannelFilter($event)"
          [options]="channels" [showClear]="true" placeholder="All channels" appendTo="body" />
      </div>
      <span class="count">{{ filtered().length }} of {{ rows().length }}</span>
    </div>

    <p-table [value]="filtered()" [loading]="loading()" dataKey="id" [rows]="10"
      [paginator]="filtered().length > 10" [rowsPerPageOptions]="[10, 25, 50]" styleClass="p-datatable-sm" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}">
      <ng-template pTemplate="header">
        <tr>
          <th>Channel</th>
          <th>Provider</th>
          <th>Scope</th>
          <th>Sender / From</th>
          <th>Active</th>
          <th class="ta-r">Actions</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-r>
        <tr>
          <td><p-tag [value]="r.channel" severity="info" /></td>
          <td class="name">{{ r.providerName }}</td>
          <td>
            @if (r.partnerId == null) {
              <p-tag value="Global" severity="contrast"
                pTooltip="Default used when no partner-specific row exists." />
            } @else {
              <span pTooltip="Overrides the Global default for this channel.">{{ scopeLabel(r) }}</span>
            }
          </td>
          <td class="mono">{{ r.channel === 'EMAIL' ? (r.fromAddress || '—') : (r.senderId || '—') }}</td>
          <td>
            <span class="active-toggle" (click)="toggleActive(r)" pTooltip="Click to toggle">
              <p-tag [value]="r.isActive ? 'Active' : 'Inactive'"
                [severity]="r.isActive ? 'success' : 'secondary'" />
            </span>
          </td>
          <td class="ta-r actions">
            <p-button icon="pi pi-pencil" [rounded]="true" [text]="true" severity="secondary"
              pTooltip="Edit" (onClick)="openEdit(r)" />
            <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger"
              pTooltip="Delete" (onClick)="remove(r)" />
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6">
            <div class="state"><i class="pi pi-inbox"></i>
              <p>No provider configs found.</p>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </div>
</div>

<!-- ===================== ADD / EDIT MODAL ===================== -->
<p-dialog [visible]="showModal()" (visibleChange)="showModal.set($event)" [modal]="true" [draggable]="true"
  [resizable]="false" [closable]="true" [dismissableMask]="false"
  [header]="editing() ? 'Edit Provider Config' : 'New Provider Config'" styleClass="menu-dialog"
  [style]="{ width: '620px' }" [breakpoints]="{ '620px': '94vw' }">
  <form [formGroup]="form" class="menu-form">
    <div class="grid-2">
      <div class="field">
        <label>Channel <span class="req">*</span></label>
        <p-select formControlName="channel" [options]="channels" placeholder="Select channel" appendTo="body" />
        @if (form.get('channel')?.touched && form.get('channel')?.invalid) {
          <small class="err">Channel is required</small>
        }
      </div>
      <div class="field">
        <label>Scope (Partner)</label>
        <p-select formControlName="partnerId" [options]="partnerOptions()" optionLabel="label"
          optionValue="value" [filter]="true" filterBy="label" [showClear]="true"
          placeholder="Global (default)" appendTo="body" />
        <!-- The senders resolve their provider globally: NotificationMessages carries no
             PartnerId, so a partner-scoped row cannot be matched at send time. Say so rather
             than let an admin configure one and wonder why nothing changes. -->
        <small class="hint-muted">Leave blank for the Global default.</small>
        @if (form.get('partnerId')?.value) {
          <small class="err">
            Partner-scoped providers are not used yet — sending always resolves the Global
            provider for the channel. Leave this blank until per-partner sending is supported.
          </small>
        }
      </div>
    </div>

    <div class="field">
      <label>Provider Name <span class="req">*</span></label>
      <input pInputText type="text" formControlName="providerName" placeholder="MSG91, Twilio, SMTP, MetaCloud...">
      @if (form.get('providerName')?.touched && form.get('providerName')?.invalid) {
        <small class="err">Provider name is required</small>
      }
    </div>

    <div class="field">
      <label>Base URL</label>
      <input pInputText type="text" formControlName="baseUrl" placeholder="API base URL / SMTP host">
    </div>

    <!-- Secrets -->
    <div class="grid-2">
      <div class="field">
        <label>API Key</label>
        <div class="secret">
          <input pInputText [type]="showApiKey() ? 'text' : 'password'" formControlName="apiKey"
            autocomplete="off" placeholder="••••••••">
          <button type="button" class="eye" (click)="showApiKey.set(!showApiKey())" tabindex="-1">
            <i class="pi" [class.pi-eye]="!showApiKey()" [class.pi-eye-slash]="showApiKey()"></i>
          </button>
        </div>
      </div>
      <div class="field">
        <label>API Secret</label>
        <div class="secret">
          <input pInputText [type]="showApiSecret() ? 'text' : 'password'" formControlName="apiSecret"
            autocomplete="off" placeholder="••••••••">
          <button type="button" class="eye" (click)="showApiSecret.set(!showApiSecret())" tabindex="-1">
            <i class="pi" [class.pi-eye]="!showApiSecret()" [class.pi-eye-slash]="showApiSecret()"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- No Auth Token input for WhatsApp: the sender PREFERS it over the API key when both
         are set, so a stray token silently sends the wrong credential. A row that still
         carries one gets a warning + a way to drop it instead. -->
    @if (selectedChannel === 'WHATSAPP' && form.get('authToken')?.value) {
      <div class="field">
        <small class="err">
          This row stores a legacy Auth Token, which outranks the API key at send time.
          If this provider authenticates by API key, clear it.
        </small>
        <p-button label="Clear stored token" icon="pi pi-times" size="small" severity="danger"
          [outlined]="true" (onClick)="clearAuthToken()" />
      </div>
    }

    <!-- SMS / WhatsApp sender id (contextual) -->
    @if (selectedChannel === 'SMS' || selectedChannel === 'WHATSAPP') {
      <div class="field">
        <label>{{ selectedChannel === 'SMS' ? 'Sender ID' : 'Sender / Channel ID' }}</label>
        <input pInputText type="text" formControlName="senderId"
          [placeholder]="selectedChannel === 'SMS' ? 'DLT sender id' : 'WhatsApp phone-number id / gateway channel id'">
        @if (selectedChannel === 'WHATSAPP') {
          <small class="hint-muted">Requests go to <span class="mono-inline">{{ '{base}/{sender}/messages' }}</span> — Meta's phone-number id, or the bridge gateway's channel id.</small>
        }
      </div>
    }

    <!-- Email from address (contextual) -->
    @if (selectedChannel === 'EMAIL') {
      <div class="field">
        <label>From Address</label>
        <input pInputText type="text" formControlName="fromAddress" placeholder="noreply@example.com / Display Name">
      </div>
    }

    @if (selectedChannel === 'WHATSAPP' && !extraParseFailed()) {
      <!-- The extras an admin actually needs, by name — the JSON is rebuilt on save and any
           other keys already in it (TemplateName, ...) are preserved untouched. -->
      <div class="grid-2">
        <div class="field">
          <label>Auth Header</label>
          <input pInputText type="text" formControlName="waAuthHeader" placeholder="Authorization (default)">
          <small class="hint-muted">Blank = Meta's "Authorization: Bearer". A bridge gateway
            wanting its own header sets its name here, e.g. x-api-key.</small>
        </div>
        <div class="field">
          <label>Default Template Language</label>
          <input pInputText type="text" formControlName="waTemplateLang" placeholder="en">
          <small class="hint-muted">Used when a template row doesn't name its own language.</small>
        </div>
      </div>
    } @else {
      <div class="field">
        <label>Extra Config (JSON)</label>
        <textarea class="form-textarea mono" rows="3" formControlName="extraConfigJson"
          placeholder='{ "region": "ap-south-1" }'></textarea>
        @if (extraParseFailed()) {
          <small class="err">The stored value is not valid JSON, so it must be edited here raw.
            Fix it and the named fields return next time.</small>
        } @else {
          <small class="hint-muted">Optional provider-specific extras. Must be valid JSON.</small>
        }
      </div>
    }

    <p-checkbox [binary]="true" formControlName="isActive" label="Active (used by senders)" styleClass="check" />

    <div class="modal-actions">
      <p-button label="Cancel" [outlined]="true" (onClick)="closeModal()" />
      <p-button label="Save" (onClick)="save()" [loading]="saving()" />
    </div>
  </form>
</p-dialog>
